import { FC } from 'react';
import type { UsageDayPoint } from '@libs/types';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import { AdminPanel, AdminPanelHead } from '../AdminPanel';
import { adminColors } from '../adminTheme';

import { formatUsd } from './usageFormat';

interface Props {
  points: UsageDayPoint[];
  isLoading?: boolean;
  title: string;
  sub?: string;
}

const CHART_HEIGHT = 240;

/**
 * Daily provider cost over the selected window.
 *
 * The series is dense (zero-filled server-side), which matters: a gap in a cost
 * chart reads as missing data, whereas an explicit zero reads as "we spent
 * nothing that day".
 *
 * RTL: `@mui/x-charts` does not mirror, and that is left alone deliberately —
 * a time axis running left-to-right is the convention in Arabic data
 * visualisation too. What does matter is that tick and tooltip labels use Latin
 * digits, which the hardcoded `en-US` formatter guarantees.
 */
export const UsageTrendChart: FC<Props> = observer(({ points, isLoading, title, sub }) => {
  const { t } = useTrans();
  const theme = useTheme();
  const c = adminColors(theme);

  const hasData = points.some((p) => p.costUsd > 0);

  return (
    <AdminPanel>
      <AdminPanelHead title={title} sub={sub} iconColor={c.azure} />
      <Box sx={{ px: 1, py: 1.5 }}>
        {isLoading ? (
          // Hold the chart's height while loading. Letting it collapse to 0 and
          // snap back is the layout jump the DataTable doc comment warns about.
          <Skeleton variant="rounded" height={CHART_HEIGHT} sx={{ mx: 1.25, borderRadius: 2 }} />
        ) : !hasData ? (
          <Box
            sx={{
              height: CHART_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: 13, color: c.faint }}>
              {t('app.admin.usage.trend.empty')}
            </Typography>
          </Box>
        ) : (
          <LineChart
            height={CHART_HEIGHT}
            margin={{ left: 8, right: 16, top: 12, bottom: 24 }}
            grid={{ horizontal: true }}
            xAxis={[
              {
                data: points.map((p) => p.day),
                scaleType: 'point',
                // Only month-day: full ISO dates collide on a 30-point axis.
                valueFormatter: (day: string) => day.slice(5),
              },
            ]}
            yAxis={[{ valueFormatter: (v: number) => formatUsd(v) }]}
            series={[
              {
                data: points.map((p) => p.costUsd),
                area: true,
                showMark: false,
                color: c.azure,
                valueFormatter: (v: number | null) => formatUsd(v),
              },
            ]}
            sx={{
              '& .MuiChartsAxis-tickLabel': { fontSize: 10.5, fill: c.faint },
              '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: c.border },
              '& .MuiChartsGrid-line': { stroke: c.border, strokeDasharray: '3 3' },
              '& .MuiAreaElement-root': { fillOpacity: 0.12 },
            }}
          />
        )}
      </Box>
    </AdminPanel>
  );
});
